/**
 * Date-range presets for the analytics stores — pure date arithmetic, deliberately kept
 * in a module that imports NOTHING.
 *
 * Why this file exists (not cosmetic):
 *
 *   analytics/busiestTimeService → lib/core/api → stores/authStore
 *     → stores/busiestTimeStore → back to analytics/busiestTimeService
 *
 * `authStore` imports eleven stores so it can reset them on logout, `core/api` imports
 * `authStore` for the token, and the analytics services import `core/api`. That closes a
 * loop. A loop is survivable as long as nothing in it is READ while the modules are still
 * initialising — but `busiestTimeStore` and `insightsStore` both call
 * `Service.getDateRangePresets()` inside `create()`, which runs at module-evaluation time.
 * Whichever module the bundler happens to enter the cycle from decides whether that call
 * sees the real class or `undefined`; the app has been getting away with it because its
 * entry point happens to import in a lucky order, and a test that imports
 * `busiestTimeService` FIRST reproduces the crash immediately
 * ("Cannot read properties of undefined (reading 'getDateRangePresets')").
 *
 * Code-splitting changes import order, so this was one lazy route away from breaking at
 * runtime rather than only in tests.
 *
 * The stores now take their initial range from here, which touches no store and no API
 * client, so the eval-time read can no longer land inside the cycle. The services keep
 * their `getDateRangePresets()` methods and delegate here, so every existing caller and
 * test is unaffected.
 *
 * The two functions are NOT merged. They disagree today — Busiest Time formats in UTC via
 * toISOString(), Insights formats in the viewer's local timezone — and for a merchant east
 * of UTC those produce different days. Unifying them is a real product decision about
 * which day a chart is reporting on, not a refactor, so both are preserved exactly as they
 * were. See the note on {@link busiestTimeDateRangePresets}.
 */

export interface DateRangePreset {
  startTime: string;
  endTime: string;
}

export type DateRangePresets = Record<string, DateRangePreset>;

/**
 * UTC-based, as Busiest Time has always done it.
 *
 * NOTE: `toISOString()` converts to UTC before taking the date part, so for a merchant in
 * UTC+10 this rolls the day over ten hours early — "today" becomes yesterday's date for
 * most of their working day. {@link insightsDateRangePresets} below uses local time for
 * the same concept. That inconsistency is pre-existing and left untouched here on purpose:
 * this change is about breaking an import cycle, and quietly switching one of the two
 * would move every Busiest Time chart by a day.
 */
export function busiestTimeDateRangePresets(): DateRangePresets {
  const today = new Date();
  const formatDate = (date: Date) => date.toISOString().split('T')[0];

  return {
    last7Days: {
      startTime: formatDate(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)),
      endTime: formatDate(today),
    },
    last30Days: {
      startTime: formatDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)),
      endTime: formatDate(today),
    },
    last3Months: {
      startTime: formatDate(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)),
      endTime: formatDate(today),
    },
  };
}

/** Local-timezone based, as Insights has always done it. */
export function insightsDateRangePresets(): DateRangePresets {
  const padTwo = (value: number) => String(value).padStart(2, '0');
  const formatLocalDate = (date: Date): string =>
    `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

  const today = new Date();

  return {
    last7Days: {
      startTime: formatLocalDate(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)),
      endTime: formatLocalDate(today),
    },
    last30Days: {
      startTime: formatLocalDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)),
      endTime: formatLocalDate(today),
    },
    last3Months: {
      startTime: formatLocalDate(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)),
      endTime: formatLocalDate(today),
    },
  };
}
